import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid2 as Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AccountBalance,
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  ExpandMore,
  Link as LinkIcon,
  Api,
  Email,
  Security,
  Settings,
  CheckCircle,
  Warning,
  Info,
  Code,
  ConnectWithoutContact,
  ArrowBack,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  loadPpSettings,
  savePpSettings,
} from "../../../../store/actions/ppActions";

const PpCityReportingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const ppState = useSelector((state) => state[currentSession]?.pp);
  const settings = ppState?.settings?.data || {};

  const [cityProfiles, setCityProfiles] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [testConnectionOpen, setTestConnectionOpen] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);

  // Enhanced city profiles with reporting interfaces
  const defaultCityProfiles = [
    {
      id: "duval",
      name: "Duval County",
      state: "FL",
      reportingMethod: "portal", // portal, api, email
      portalUrl: "https://duval.county.gov/permits",
      apiEndpoint: "https://api.duval.gov/v1/inspections",
      apiKey: "",
      emailAddress: "inspections@duval.gov",
      autoReport: true,
      reportingSchedule: "daily", // daily, hourly, manual
      requiredFields: [
        "permit_number",
        "address",
        "contractor_license",
        "inspection_type",
      ],
      customFields: [],
      reportingDelay: 24, // hours
      authMethod: "api_key", // api_key, oauth, basic_auth
      credentials: {
        username: "",
        password: "",
        clientId: "",
        clientSecret: "",
      },
      lastSyncDate: null,
      connectionStatus: "not_tested",
      supportedInspectionTypes: [
        "foundation",
        "framing",
        "electrical",
        "mechanical",
        "plumbing",
        "final",
      ],
      businessHours: {
        start: "08:00",
        end: "17:00",
        timezone: "America/New_York",
        weekends: false,
      },
    },
    {
      id: "st_johns",
      name: "St Johns County",
      state: "FL",
      reportingMethod: "email",
      portalUrl: "https://stjohns.county.gov",
      apiEndpoint: "",
      apiKey: "",
      emailAddress: "permits@stjohns.gov",
      autoReport: false,
      reportingSchedule: "manual",
      requiredFields: ["permit_number", "address", "contractor_license"],
      customFields: [
        { name: "property_owner", required: true },
        { name: "job_value", required: false },
      ],
      reportingDelay: 48,
      authMethod: "none",
      credentials: {},
      lastSyncDate: null,
      connectionStatus: "not_applicable",
      supportedInspectionTypes: ["foundation", "framing", "final"],
      businessHours: {
        start: "09:00",
        end: "16:00",
        timezone: "America/New_York",
        weekends: false,
      },
    },
    {
      id: "clay",
      name: "Clay County",
      state: "FL",
      reportingMethod: "api",
      portalUrl: "https://clay.county.gov",
      apiEndpoint: "https://api.clay.gov/inspections",
      apiKey: "",
      emailAddress: "",
      autoReport: true,
      reportingSchedule: "hourly",
      requiredFields: ["permit_number", "address", "inspection_date"],
      customFields: [],
      reportingDelay: 12,
      authMethod: "oauth",
      credentials: {
        clientId: "",
        clientSecret: "",
      },
      lastSyncDate: null,
      connectionStatus: "not_configured",
      supportedInspectionTypes: [
        "foundation",
        "framing",
        "electrical",
        "mechanical",
        "plumbing",
        "insulation",
        "drywall",
        "final",
      ],
      businessHours: {
        start: "07:30",
        end: "16:30",
        timezone: "America/New_York",
        weekends: false,
      },
    },
    {
      id: "nassau",
      name: "Nassau County",
      state: "FL",
      reportingMethod: "portal",
      portalUrl: "https://nassau.county.gov/building",
      apiEndpoint: "",
      apiKey: "",
      emailAddress: "building@nassau.gov",
      autoReport: false,
      reportingSchedule: "manual",
      requiredFields: [
        "permit_number",
        "address",
        "contractor_license",
        "property_owner",
      ],
      customFields: [
        { name: "subdivision", required: false },
        { name: "lot_number", required: false },
      ],
      reportingDelay: 72,
      authMethod: "basic_auth",
      credentials: {
        username: "",
        password: "",
      },
      lastSyncDate: null,
      connectionStatus: "not_configured",
      supportedInspectionTypes: ["foundation", "framing", "final"],
      businessHours: {
        start: "08:00",
        end: "17:00",
        timezone: "America/New_York",
        weekends: false,
      },
    },
  ];

  useEffect(() => {
    setCityProfiles(defaultCityProfiles);
    dispatch(loadPpSettings());
  }, [dispatch]);

  const handleSaveCity = async () => {
    if (!selectedCity) return;

    try {
      const updatedProfiles = cityProfiles.map((profile) =>
        profile.id === selectedCity.id ? selectedCity : profile
      );

      setCityProfiles(updatedProfiles);

      // Save to settings
      const updatedSettings = {
        ...settings,
        cityReporting: updatedProfiles,
      };

      await dispatch(savePpSettings(updatedSettings));
      setEditDialogOpen(false);
      setSelectedCity(null);
    } catch (error) {
      console.error("Failed to save city profile:", error);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedCity) return;

    try {
      setConnectionResult(null);

      // Mock connection test
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const success = Math.random() > 0.3; // 70% success rate for demo

      setConnectionResult({
        success,
        message: success
          ? "Connection successful! Integration is working properly."
          : "Connection failed. Please check your credentials and endpoint configuration.",
        timestamp: new Date().toISOString(),
      });

      // Update connection status
      setSelectedCity((prev) => ({
        ...prev,
        connectionStatus: success ? "connected" : "failed",
        lastSyncDate: success ? new Date().toISOString() : prev.lastSyncDate,
      }));
    } catch (error) {
      setConnectionResult({
        success: false,
        message: "Network error during connection test.",
        timestamp: new Date().toISOString(),
      });
    }
  };

  const getConnectionStatusChip = (status) => {
    const statusConfig = {
      connected: {
        color: "success",
        label: "Connected",
        icon: <CheckCircle />,
      },
      failed: { color: "error", label: "Failed", icon: <Warning /> },
      not_configured: {
        color: "warning",
        label: "Not Configured",
        icon: <Settings />,
      },
      not_tested: { color: "default", label: "Not Tested", icon: <Info /> },
      not_applicable: { color: "default", label: "N/A", icon: <Info /> },
    };

    const config = statusConfig[status] || statusConfig.not_tested;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  const getReportingMethodIcon = (method) => {
    const iconMap = {
      portal: <LinkIcon />,
      api: <Api />,
      email: <Email />,
    };
    return iconMap[method] || <Info />;
  };

  const getReportingMethodColor = (method) => {
    const colorMap = {
      portal: "primary",
      api: "info",
      email: "secondary",
    };
    return colorMap[method] || "default";
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/pp/settings")}>
          <ArrowBack />
        </IconButton>
        <Typography color="primary" variant="h4">
          City Reporting Interfaces
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Configure reporting interfaces for North Florida counties (Duval, St
          Johns, Clay, Nassau). Each city has specific requirements for
          inspection reporting and notifications.
        </Typography>
      </Alert>

      {/* City Profiles */}
      <Grid container spacing={3}>
        {cityProfiles.map((city) => (
          <Grid key={city.id} size={{ xs: 12, lg: 6 }}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {city.name}
                    </Typography>
                    <Box
                      sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}
                    >
                      <Chip
                        icon={getReportingMethodIcon(city.reportingMethod)}
                        label={city.reportingMethod.toUpperCase()}
                        color={getReportingMethodColor(city.reportingMethod)}
                        size="small"
                      />
                      {getConnectionStatusChip(city.connectionStatus)}
                      <Chip
                        label={city.autoReport ? "Auto Report" : "Manual"}
                        color={city.autoReport ? "success" : "warning"}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => {
                      setSelectedCity(city);
                      setEditDialogOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Box>

                {/* Configuration Summary */}
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Reporting Method"
                      secondary={
                        city.reportingMethod.charAt(0).toUpperCase() +
                        city.reportingMethod.slice(1)
                      }
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemText
                      primary="Schedule"
                      secondary={`${city.reportingSchedule} (${city.reportingDelay}h delay)`}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemText
                      primary="Required Fields"
                      secondary={`${city.requiredFields.length} configured`}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemText
                      primary="Supported Inspections"
                      secondary={`${city.supportedInspectionTypes.length} types`}
                    />
                  </ListItem>

                  {city.lastSyncDate && (
                    <ListItem>
                      <ListItemText
                        primary="Last Sync"
                        secondary={new Date(city.lastSyncDate).toLocaleString()}
                      />
                    </ListItem>
                  )}
                </List>

                {/* Quick Actions */}
                <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                  {city.reportingMethod !== "email" && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ConnectWithoutContact />}
                      onClick={() => {
                        setSelectedCity(city);
                        setTestConnectionOpen(true);
                      }}
                    >
                      Test Connection
                    </Button>
                  )}

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<LinkIcon />}
                    href={city.portalUrl}
                    target="_blank"
                  >
                    Open Portal
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Edit City Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Configure City Reporting - {selectedCity?.name}
        </DialogTitle>
        <DialogContent>
          {selectedCity && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Basic Settings */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" gutterBottom>
                  Basic Configuration
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Reporting Method</InputLabel>
                  <Select
                    value={selectedCity.reportingMethod}
                    label="Reporting Method"
                    onChange={(e) =>
                      setSelectedCity((prev) => ({
                        ...prev,
                        reportingMethod: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="portal">Portal Integration</MenuItem>
                    <MenuItem value="api">API Integration</MenuItem>
                    <MenuItem value="email">Email Reporting</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Portal URL"
                  value={selectedCity.portalUrl}
                  onChange={(e) =>
                    setSelectedCity((prev) => ({
                      ...prev,
                      portalUrl: e.target.value,
                    }))
                  }
                  sx={{ mb: 2 }}
                />

                {selectedCity.reportingMethod === "api" && (
                  <TextField
                    fullWidth
                    label="API Endpoint"
                    value={selectedCity.apiEndpoint}
                    onChange={(e) =>
                      setSelectedCity((prev) => ({
                        ...prev,
                        apiEndpoint: e.target.value,
                      }))
                    }
                    sx={{ mb: 2 }}
                  />
                )}

                {selectedCity.reportingMethod === "email" && (
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={selectedCity.emailAddress}
                    onChange={(e) =>
                      setSelectedCity((prev) => ({
                        ...prev,
                        emailAddress: e.target.value,
                      }))
                    }
                    sx={{ mb: 2 }}
                  />
                )}

                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedCity.autoReport}
                        onChange={(e) =>
                          setSelectedCity((prev) => ({
                            ...prev,
                            autoReport: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Auto Report"
                  />

                  <FormControl>
                    <InputLabel>Schedule</InputLabel>
                    <Select
                      value={selectedCity.reportingSchedule}
                      label="Schedule"
                      onChange={(e) =>
                        setSelectedCity((prev) => ({
                          ...prev,
                          reportingSchedule: e.target.value,
                        }))
                      }
                      disabled={!selectedCity.autoReport}
                    >
                      <MenuItem value="hourly">Hourly</MenuItem>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="manual">Manual Only</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <TextField
                  fullWidth
                  type="number"
                  label="Reporting Delay (hours)"
                  value={selectedCity.reportingDelay}
                  onChange={(e) =>
                    setSelectedCity((prev) => ({
                      ...prev,
                      reportingDelay: parseInt(e.target.value),
                    }))
                  }
                  helperText="Minimum hours before inspection can be reported to city"
                  inputProps={{ min: 1, max: 168 }}
                />
              </Grid>

              {/* Authentication */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" gutterBottom>
                  Authentication
                </Typography>

                {selectedCity.reportingMethod !== "email" && (
                  <>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Auth Method</InputLabel>
                      <Select
                        value={selectedCity.authMethod}
                        label="Auth Method"
                        onChange={(e) =>
                          setSelectedCity((prev) => ({
                            ...prev,
                            authMethod: e.target.value,
                          }))
                        }
                      >
                        <MenuItem value="none">No Authentication</MenuItem>
                        <MenuItem value="api_key">API Key</MenuItem>
                        <MenuItem value="oauth">OAuth 2.0</MenuItem>
                        <MenuItem value="basic_auth">Basic Auth</MenuItem>
                      </Select>
                    </FormControl>

                    {selectedCity.authMethod === "api_key" && (
                      <TextField
                        fullWidth
                        label="API Key"
                        type="password"
                        value={selectedCity.apiKey}
                        onChange={(e) =>
                          setSelectedCity((prev) => ({
                            ...prev,
                            apiKey: e.target.value,
                          }))
                        }
                        sx={{ mb: 2 }}
                      />
                    )}

                    {selectedCity.authMethod === "oauth" && (
                      <>
                        <TextField
                          fullWidth
                          label="Client ID"
                          value={selectedCity.credentials.clientId}
                          onChange={(e) =>
                            setSelectedCity((prev) => ({
                              ...prev,
                              credentials: {
                                ...prev.credentials,
                                clientId: e.target.value,
                              },
                            }))
                          }
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          label="Client Secret"
                          type="password"
                          value={selectedCity.credentials.clientSecret}
                          onChange={(e) =>
                            setSelectedCity((prev) => ({
                              ...prev,
                              credentials: {
                                ...prev.credentials,
                                clientSecret: e.target.value,
                              },
                            }))
                          }
                          sx={{ mb: 2 }}
                        />
                      </>
                    )}

                    {selectedCity.authMethod === "basic_auth" && (
                      <>
                        <TextField
                          fullWidth
                          label="Username"
                          value={selectedCity.credentials.username}
                          onChange={(e) =>
                            setSelectedCity((prev) => ({
                              ...prev,
                              credentials: {
                                ...prev.credentials,
                                username: e.target.value,
                              },
                            }))
                          }
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          label="Password"
                          type="password"
                          value={selectedCity.credentials.password}
                          onChange={(e) =>
                            setSelectedCity((prev) => ({
                              ...prev,
                              credentials: {
                                ...prev.credentials,
                                password: e.target.value,
                              },
                            }))
                          }
                          sx={{ mb: 2 }}
                        />
                      </>
                    )}
                  </>
                )}

                {/* Business Hours */}
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Business Hours
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      fullWidth
                      label="Start Time"
                      type="time"
                      value={selectedCity.businessHours.start}
                      onChange={(e) =>
                        setSelectedCity((prev) => ({
                          ...prev,
                          businessHours: {
                            ...prev.businessHours,
                            start: e.target.value,
                          },
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      fullWidth
                      label="End Time"
                      type="time"
                      value={selectedCity.businessHours.end}
                      onChange={(e) =>
                        setSelectedCity((prev) => ({
                          ...prev,
                          businessHours: {
                            ...prev.businessHours,
                            end: e.target.value,
                          },
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Required Fields */}
              <Grid size={{ xs: 12 }}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">
                      Required Fields ({selectedCity.requiredFields.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      These fields will be required when reporting inspections
                      to this city.
                    </Alert>
                    {/* Field configuration would go here */}
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Supported Inspection Types */}
              <Grid size={{ xs: 12 }}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">
                      Supported Inspection Types (
                      {selectedCity.supportedInspectionTypes.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedCity.supportedInspectionTypes.map((type) => (
                        <Chip
                          key={type}
                          label={type.replace("_", " ")}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          {selectedCity?.reportingMethod !== "email" && (
            <Button
              variant="outlined"
              startIcon={<ConnectWithoutContact />}
              onClick={() => setTestConnectionOpen(true)}
            >
              Test Connection
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSaveCity}
            startIcon={<Save />}
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Connection Dialog */}
      <Dialog
        open={testConnectionOpen}
        onClose={() => setTestConnectionOpen(false)}
      >
        <DialogTitle>Test Connection - {selectedCity?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Testing connection to {selectedCity?.name} reporting interface...
          </Typography>

          {connectionResult ? (
            <Alert severity={connectionResult.success ? "success" : "error"}>
              {connectionResult.message}
            </Alert>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <div>Testing connection...</div>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestConnectionOpen(false)}>Close</Button>
          {!connectionResult && (
            <Button variant="contained" onClick={handleTestConnection}>
              Test Now
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PpCityReportingPage;
